import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { getSectionsList } from '../../../sections';

// Default thumbnail for sections without custom preview
const DefaultThumbnail = () => (
  <div className="w-full h-20 rounded-lg bg-zinc-100 dark:bg-zinc-800 p-2 flex items-center justify-center">
    <div className="w-8 h-8 bg-zinc-200 dark:bg-zinc-700 rounded" />
  </div>
);

const SectionLibraryModal = ({ isOpen, onClose, onAddSection }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);
  const isDragging = useRef(false);

  // Animate in/out
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      setDragY(0);
      const timeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setActiveCategory('all');
    }
  }, [isOpen]);

  // Swipe to close handlers
  const handleTouchStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
    isDragging.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - dragStartY.current;
    if (diff > 0) {
      setDragY(diff);
    }
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    if (dragY > 100) {
      onClose();
    }
    setDragY(0);
  };

  if (!shouldRender) return null;

  const registrySections = getSectionsList();

  const categories = [
    { id: 'all', name: 'Все', icon: 'LayoutGrid' },
    { id: 'hero', name: 'Hero', icon: 'Sparkles' },
    { id: 'navigation', name: 'Навигация', icon: 'Menu' },
    { id: 'content', name: 'Контент', icon: 'FileText' },
    { id: 'media', name: 'Медиа', icon: 'Images' },
    { id: 'pricing', name: 'Цены', icon: 'DollarSign' },
    { id: 'forms', name: 'Формы', icon: 'Mail' },
    { id: 'layout', name: 'Разметка', icon: 'LayoutTemplate' },
    { id: 'widgets', name: 'Виджеты', icon: 'Calendar' },
  ];

  const legacyComponents = [
    {
      id: 'about-1',
      name: 'О нас с изображением',
      category: 'content',
      icon: 'Info',
      description: 'Секция с описанием и боковым изображением',
    },
  ];

  const allComponents = [
    ...registrySections.map((section) => ({
      id: section.id,
      name: section.name,
      category: section.category,
      icon: section.icon,
      description: section.description || `Секция: ${section.name}`,
      isRegistrySection: true,
      defaultData: section.defaultData,
      Thumbnail: section.Thumbnail,
    })),
    ...legacyComponents,
  ];

  const filtered = allComponents.filter((c) => {
    const matchesSearch =
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || c.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSelect = (component) => {
    onAddSection(component);
    onClose();
  };

  const modalContent = (
    <>
      {/* Search + category pills */}
      <div className="p-4 border-b border-border flex-shrink-0 space-y-3">
        <Input
          type="search"
          placeholder="Поиск секций..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex-shrink-0 ${
                activeCategory === cat.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={cat.icon} size={14} />
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Grid of cards with previews */}
      <div className="flex-1 overflow-y-auto p-4">
        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((component) => (
              <button
                key={component.id}
                onClick={() => handleSelect(component)}
                className="flex flex-col rounded-xl border border-border hover:border-primary hover:shadow-md transition-all text-left group overflow-hidden"
              >
                {/* Preview */}
                <div className="p-2 bg-muted/50 group-hover:bg-muted transition-colors">
                  {component.Thumbnail ? <component.Thumbnail /> : <DefaultThumbnail />}
                </div>
                {/* Label */}
                <div className="p-3">
                  <p className="font-medium text-sm mb-0.5 truncate">{component.name}</p>
                  <p className="text-xs text-text-secondary line-clamp-1">
                    {component.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Search" size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm text-text-secondary">Секции не найдены</p>
          </div>
        )}
      </div>
    </>
  );

  return createPortal(
    <div className="fixed inset-0 z-[1500]">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-background/60 backdrop-blur-sm transition-opacity duration-200 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Desktop: centered modal */}
      <div className={`hidden md:flex fixed inset-4 items-center justify-center pointer-events-none`}>
        <div className={`pointer-events-auto bg-card border border-border rounded-2xl shadow-elevation-4 w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden transition-all duration-200 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
            <h2 className="text-lg font-semibold">Добавить секцию</h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-muted rounded-lg transition-colors"
              aria-label="Закрыть"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          {modalContent}
        </div>
      </div>

      {/* Mobile: bottom sheet */}
      <div
        className={`md:hidden fixed bottom-0 left-0 right-0 max-h-[70vh] flex flex-col bg-card border-t border-border rounded-t-2xl shadow-elevation-4 ${
          dragY > 0 ? '' : 'transition-transform duration-300 ease-out'
        } ${visible && dragY === 0 ? 'translate-y-0' : ''} ${!visible ? 'translate-y-full' : ''}`}
        style={dragY > 0 ? { transform: `translateY(${dragY}px)` } : undefined}
      >
        {/* Drag handle */}
        <div
          className="flex justify-center py-3 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-10 h-1 bg-border rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold">Добавить секцию</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded-lg transition-colors"
            aria-label="Закрыть"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        {modalContent}
      </div>
    </div>,
    document.body
  );
};

export default SectionLibraryModal;
